import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';
import * as yup from 'yup';

import FormControls from 'components/FormControls/FormControls';
import Panel from 'components/Panel/Panel';
import TextArea from 'components/TextArea/TextArea';
import useNotification from 'hooks/useNotification';
import useUpdateAssessmentDetails from 'hooks/useUpdateAssessmentDetails';
import { t } from 'locales/i18n';

enum FormFields {
  ASSESSMENT_NAME = 'name',
  COHORT_VISION = 'cohortVision',
}

interface FormData {
  [FormFields.ASSESSMENT_NAME]: string;
  [FormFields.COHORT_VISION]: string;
}

interface OverviewAssessmentDetailsFormProps {
  assessmentName: string;
  cohortVision: string;
  interviewUid: string;
  isLoading: boolean;
  isPractice: boolean;
}

const wordCount = (words = '') =>
  words.split(' ').filter((n) => n !== '').length;

const OverviewAssessmentDetailsForm = ({
  assessmentName,
  cohortVision,
  interviewUid,
  isLoading,
  isPractice,
}: OverviewAssessmentDetailsFormProps) => {
  const { updateAssessmentDetails, isLoading: isLoadingForm } =
    useUpdateAssessmentDetails();
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const queryClient = useQueryClient();

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.ASSESSMENT_NAME]: yup
          .string()
          .required(t('Assessment name is required'))
          .max(60, t('Assessment name cannot exceed 60 characters'))
          .test(
            'emptyString',
            t('Assessment name cannot be empty'),
            (value) => value?.trim() !== ''
          ),
        [FormFields.COHORT_VISION]: yup
          .string()
          .test(
            'wordCount',
            t('Cohort vision cannot exceed 50 words'),
            (value) => wordCount(value) <= 50
          ),
      }),
    [FormFields.ASSESSMENT_NAME, FormFields.COHORT_VISION]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    values: {
      [FormFields.ASSESSMENT_NAME]: assessmentName,
      [FormFields.COHORT_VISION]: cohortVision,
    },
  });

  const onSubmit: SubmitHandler<FormData> = async ({ name, cohortVision }) => {
    await updateAssessmentDetails(
      {
        interviewUid,
        name,
        cohortVision,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentDetails', interviewUid],
          });
          showSuccessNotification('Success', 'Assessment updated successfully');
        },
        onError: (error) => {
          showErrorNotification(
            'An error occurred while updating assessment',
            error.message
          );
        },
      }
    );
  };

  return (
    <Panel
      title={isPractice ? t('Practice Details') : t('Assessment Details')}
      isFocused={isDirty}
      panelClassName="overflow-visible"
    >
      {isLoading ? (
        <Skeleton height={130} count={1} />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={isLoadingForm}>
            <TextArea
              label={isPractice ? t('Practice Name') : t('Assessment Name')}
              id={FormFields.ASSESSMENT_NAME}
              value={watch(FormFields.ASSESSMENT_NAME)}
              errors={errors[FormFields.ASSESSMENT_NAME]}
              {...register(FormFields.ASSESSMENT_NAME)}
              dataTestId={
                isPractice ? 'practice-name-input' : 'assessment-name-input'
              }
            />
            {!isPractice && (
              <TextArea
                label={t('Cohort Vision')}
                tooltipText={t(
                  "A 'cohort vision' is a short, qualitative description of the goals you envision successful graduates of your program achieving. This should be in line with the culture, values, and mission statement of your program, school, or institution."
                )}
                id={FormFields.COHORT_VISION}
                value={watch(FormFields.COHORT_VISION)}
                errors={errors[FormFields.COHORT_VISION]}
                {...register(FormFields.COHORT_VISION)}
                dataTestId="cohort-vision-input"
              />
            )}
            {isDirty && (
              <FormControls
                isLoading={isLoadingForm}
                onCancel={() => reset()}
              />
            )}
          </fieldset>
        </form>
      )}
    </Panel>
  );
};

export default OverviewAssessmentDetailsForm;
